// A day by the hour: appointments as solid blocks the length they last, with
// the way there drawn as a thin lead-in ("leave 13:40"), tasks with a time as
// hollow ones, and now as a line across. It is the Calendar's middle tab, for
// seeing the gaps in a day, not for filling them; untimed things stay on Today.
import { useEffect, useRef } from 'react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useDates, usePersonLinks, useTasks } from '@/db/hooks'
import { useNow } from '@/hooks/useNow'
import { clockAt, fixedOn, lengthOf, occurrencesBetween, type Fixed } from '@/lib/dates'
import type { DateResource } from '@/kinds/date/apply'
import type { TaskResource } from '@/kinds/task/apply'
import { addDays, dateOf, fmtDateShort, parseDate } from '@/lib/time'
import { dateHue, tintText, tokens } from '@/lib/colour'
import { isOpen, isTemplate } from '@/kinds/task/schema'
import { AppTabs, AHEAD_TABS } from '@/components/AppTabs'
import { Avatars, DateRow } from '@/components/DateBits'
import { Button, PageTitle, cx } from '@/components/ui'

/** One hour's height, in pixels. */
const HOUR = 56

type Item = {
  key: string
  start: number
  end: number
  lane?: number
  lanes?: number
}

/** Side by side where things overlap: each gets a lane, and a cluster shares the width. */
export const lay = <T extends Item>(items: T[]): T[] => {
  const sorted = [...items].sort((a, b) => a.start - b.start || b.end - a.end)
  let cluster: T[] = []
  let clusterEnd = -1
  const flush = () => {
    const n = Math.max(...cluster.map((x) => x.lane! + 1))
    for (const x of cluster) x.lanes = n
    cluster = []
  }
  for (const it of sorted) {
    if (cluster.length && it.start >= clusterEnd) flush()
    const used = new Set(cluster.filter((x) => x.end > it.start).map((x) => x.lane))
    let lane = 0
    while (used.has(lane)) lane++
    it.lane = lane
    cluster.push(it)
    clusterEnd = Math.max(clusterEnd, it.end)
  }
  if (cluster.length) flush()
  return sorted
}

type DayItem = Item &
  ({ fixed: Fixed<DateResource>; task?: undefined } | { task: TaskResource; fixed?: undefined })

export const DayView = () => {
  const navigate = useNavigate()
  const { d } = useSearch({ strict: false }) as { d?: string }
  const now = useNow(60_000)
  const today = dateOf(new Date(now))
  const day = d && /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : today
  const dates = useDates()
  const links = usePersonLinks()
  const tasks = (useTasks() ?? []).filter(
    (t) =>
      !t.deleted_at &&
      !isTemplate(t.spec) &&
      isOpen(t.status.state) &&
      t.spec.scheduled === day &&
      t.spec.scheduled_time,
  )
  const fixed = fixedOn(dates, day)
  const allDay = dates.filter((x) => !x.spec.time && occurrencesBetween(x.spec, day, day).length)
  const nowMin = new Date(now).getHours() * 60 + new Date(now).getMinutes()

  const toMin = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5))
  const items = lay<DayItem>([
    ...fixed.map((f): DayItem => ({ key: f.date.id, start: f.leave ?? f.start, end: f.end, fixed: f })),
    ...tasks.map((t): DayItem => {
      const start = toMin(t.spec.scheduled_time!)
      return { key: t.id, start, end: start + Math.max(15, t.spec.estimate_minutes ?? 30), task: t }
    }),
  ])
  // the hours drawn: a waking day at least, stretched to whatever is on it
  const first = Math.min(8 * 60, ...items.map((i) => i.start), day === today ? nowMin : Infinity)
  const last = Math.max(22 * 60, ...items.map((i) => i.end), day === today ? nowMin + 30 : 0)
  const h0 = Math.floor(first / 60)
  const h1 = Math.min(24, Math.ceil(last / 60))
  const y = (min: number) => ((min - h0 * 60) / 60) * HOUR

  // open on now (or the first thing), not on the small hours
  const anchor = useRef<HTMLDivElement>(null)
  useEffect(() => {
    anchor.current?.scrollIntoView({ block: 'center' })
  }, [day])

  const go = (to: string) =>
    void navigate({ to: '/day', search: { d: to === today ? undefined : to } as never, replace: true })
  const anchorAt = day === today ? nowMin : (items[0]?.start ?? 9 * 60)

  return (
    <div data-testid="day">
      <PageTitle>Calendar</PageTitle>
      <AppTabs tabs={AHEAD_TABS} label="Calendar" />
      <div className="flex items-center gap-2 mb-3">
        <Button
          variant="chip"
          className="px-2"
          onClick={() => go(addDays(day, -1))}
          aria-label="The day before"
        >
          <ChevronLeft size={16} />
        </Button>
        <h2 className="t-title2 flex-1 text-center" data-testid="day-title">
          {day === today
            ? 'Today'
            : parseDate(day).toLocaleDateString(undefined, {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
              })}
        </h2>
        <Button
          variant="chip"
          className="px-2"
          onClick={() => go(addDays(day, 1))}
          aria-label="The day after"
        >
          <ChevronRight size={16} />
        </Button>
      </div>
      {day !== today && (
        <div className="flex justify-center -mt-1 mb-3">
          <Button variant="chip" onClick={() => go(today)} data-testid="day-today">
            Back to today
          </Button>
        </div>
      )}

      {allDay.length > 0 && (
        <ul className="group-list mb-4" data-testid="day-allday">
          {allDay.map((x) => (
            <DateRow key={x.id} date={x} on={day} today={today} />
          ))}
        </ul>
      )}

      <div className="relative" style={{ height: (h1 - h0) * HOUR }} data-testid="day-grid">
        {Array.from({ length: h1 - h0 + 1 }, (_, i) => (
          <div key={i} className="absolute inset-x-0 flex items-start" style={{ top: i * HOUR }}>
            <span className="w-12 -mt-2 text-[11px] text-faint tabular-nums">{clockAt((h0 + i) * 60)}</span>
            <span className="flex-1 border-t border-line/60" />
          </div>
        ))}

        {/* now runs under the blocks (drawn first), so it never strikes through their words; its dot stays in the gutter */}
        {day === today && (
          <div
            className="absolute left-10 right-0 flex items-center pointer-events-none"
            style={{ top: y(nowMin) }}
            aria-label={`Now, ${clockAt(nowMin)}`}
            data-testid="day-now"
          >
            <span className="horizon-now horizon-now-sm -ml-1" />
            <span className={cx('flex-1 h-px bg-[var(--color-now)]')} />
          </div>
        )}
        <div className="absolute top-0 bottom-0 left-12 right-0">
          {items.map((it) => {
            const width = `calc(${100 / it.lanes!}% - 4px)`
            const left = `calc(${(100 / it.lanes!) * it.lane!}% + 2px)`
            if (it.fixed) {
              const f = it.fixed
              const hue = dateHue(f.date.spec.category)
              const people = links.get(f.date.id) ?? []
              // a short appointment says its time beside its name, on one line
              const compact = y(f.end) - y(f.start) < 50
              return (
                <div
                  key={it.key}
                  className="absolute"
                  // a pixel off each end, so blocks that meet still read as two
                  style={{ top: y(it.start) + 1, height: y(it.end) - y(it.start) - 2, left, width }}
                >
                  {f.leave !== undefined && (
                    // the way there: from leaving to arriving
                    <div
                      className="absolute inset-x-2 top-0 border-l-2 border-dashed pl-2 text-[11px] text-faint leading-4"
                      style={{ height: y(f.start) - y(f.leave), borderColor: tokens(hue).stripe }}
                    >
                      Leave {clockAt(f.leave)}
                    </div>
                  )}
                  <Link
                    to="/dates/$id"
                    params={{ id: f.date.id }}
                    search={{ on: day } as never}
                    className={cx(
                      'absolute inset-x-0 bottom-0 rounded-[var(--radius-card-inner)] border-l-[3px] px-2.5 overflow-hidden flex flex-col',
                      compact ? 'py-1 justify-center' : 'py-1.5',
                    )}
                    style={{
                      top: f.leave !== undefined ? y(f.start) - y(f.leave) : 0,
                      minHeight: 30,
                      // the tint over a solid panel, so the hour lines stop at the block
                      background: `linear-gradient(${tokens(hue).soft}, ${tokens(hue).soft}), var(--color-panel)`,
                      borderColor: tokens(hue).stripe,
                    }}
                    data-testid="day-fixed"
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <span
                        className="flex-1 truncate text-[14px] font-medium"
                        style={{ color: tintText(hue) }}
                      >
                        {f.date.spec.title}
                      </span>
                      {compact && (
                        <span className="text-[11px] text-muted tabular-nums">{clockAt(f.start)}</span>
                      )}
                      {people.length > 0 && <Avatars people={people} size={18} />}
                    </span>
                    <span className={cx('text-[11px] text-muted truncate', compact && 'hidden')}>
                      {clockAt(f.start)} to {clockAt(f.start + lengthOf(f.date.spec))}
                      {f.date.spec.place ? `, ${f.date.spec.place}` : ''}
                    </span>
                  </Link>
                </div>
              )
            }
            const t = it.task
            if (!t) return null
            return (
              <Link
                key={it.key}
                to="/tasks/$id"
                params={{ id: t.id }}
                className="absolute rounded-[var(--radius-card-inner)] border border-dashed border-line-strong px-2.5 py-1 text-[13px] overflow-hidden flex items-start gap-2 bg-panel/60"
                style={{
                  top: y(it.start) + 1,
                  height: Math.max(26, y(it.end) - y(it.start) - 2),
                  left,
                  width,
                }}
                data-testid="day-task"
              >
                <span
                  className="mt-1 h-3 w-3 shrink-0 rounded-full border-2 border-line-strong"
                  aria-hidden
                />
                <span className="min-w-0 flex-1 truncate">{t.spec.title}</span>
                <span className="text-[11px] text-faint tabular-nums">{t.spec.scheduled_time}</span>
              </Link>
            )
          })}
        </div>

        <div ref={anchor} className="absolute h-px w-px" style={{ top: y(anchorAt) }} aria-hidden />
      </div>
      <p className="text-xs text-faint mt-4 mb-2">
        {fmtDateShort(day, today) === 'Today'
          ? 'Anything without a time is on Today.'
          : 'Anything without a time is in the list.'}
      </p>
    </div>
  )
}
